import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import ReplayGreeting from "@/components/ReplayGreeting";
import PresenterBubble from "@/components/PresenterBubble";
import PracticeModeToggle from "@/components/PracticeModeToggle";
import PracticeModeBanner from "@/components/PracticeModeBanner";
import OhioGridMini from "@/components/OhioGridMini";
import SignatureCanvas from "@/components/SignatureCanvas";
import LeadHunter from "@/components/LeadHunter";
import StreetStrikeMap from "@/components/StreetStrikeMap";
import AudioDiagnostic from "@/components/AudioDiagnostic";
import { api } from "@/lib/api";
import { isGridInitialized, playClip, stopAllAudio, unlockAudio, GREETINGS } from "@/lib/audio";
import { toast } from "sonner";
import { LINKS as SPINE_LINKS } from "@/config/AssetSpine";
import {
  Radio, Rocket, Radar, Crosshair, ShieldCheck, ArrowRight,
  CheckCircle2, Zap, Terminal, Volume2, BookOpen, Bike,
  Sparkles, Send, ChevronRight, Target, Zap as Bolt, Globe2, Bot, Clock,
  ScrollText, MapPin,
} from "lucide-react";

// ------------------------------------------------------------
// Wingman Academy · Flight School
// Route: /training
// 4-step linear onboarding for new sales recruits.
//   Step 1 · The Mission     (Commander briefing)
//   Step 2 · The Arsenal     (Resource Library walkthrough)
//   Step 3 · Tool Training   (Lead Hunter guided sim)
//   Step 4 · Activation      (GVO pipeline + cleared for duty)
// Uses the new "wingman" character voice (deeper than Alfred).
// ------------------------------------------------------------

const STEPS = [
  { id: 1, key: "mission",     label: "The Mission",     icon: Rocket     },
  { id: 2, key: "philosophy",  label: "Grid Philosophy", icon: ScrollText },
  { id: 3, key: "arsenal",     label: "The Arsenal",     icon: Radar      },
  { id: 4, key: "streetstrike",label: "Street Strike",   icon: MapPin     },
  { id: 5, key: "tools",       label: "Tool Training",   icon: Crosshair  },
  { id: 6, key: "activation",  label: "Activation",      icon: ShieldCheck },
];

export default function Training() {
  const [stepIdx, setStepIdx] = useState(0);
  const [advancing, setAdvancing] = useState(false);
  const [manifestoSigned, setManifestoSigned] = useState(false);
  const [manifestoAt, setManifestoAt] = useState(null);
  const step = STEPS[stepIdx];
  const gateReady = isGridInitialized();

  // Amanda is the SOLE trainer in Flight School. She narrates every step.
  // On step change we cancel any in-flight audio, wait 300ms for natural
  // pacing, then fire her line for the new phase. Unmount kills any
  // audio still playing so leaving the route stops her mid-sentence.
  useEffect(() => {
    if (!gateReady) return undefined;
    stopAllAudio();
    const t = setTimeout(() => playClip("amanda", step.key), 300);
    return () => clearTimeout(t);
  }, [stepIdx, step.key, gateReady]);

  useEffect(() => stopAllAudio, []);

  const advance = () => {
    if (advancing) return;
    // Manifesto gate on philosophy step.
    if (step.key === "philosophy" && !manifestoSigned) {
      toast.error("Sign the manifesto below to advance.");
      return;
    }
    setAdvancing(true);
    unlockAudio();
    setTimeout(() => {
      setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
      setAdvancing(false);
    }, 550);
  };

  const onManifestoSign = async (dataUrl) => {
    const now = new Date().toISOString();
    setManifestoSigned(true);
    setManifestoAt(now);
    try {
      sessionStorage.setItem(
        "wa_manifesto_signed_v1",
        JSON.stringify({ signed_at: now }),
      );
    } catch (_) { /* ignore */ }
    // Fire-and-forget persistence — surface a soft toast either way.
    try {
      await api.post("/training/manifesto-signed", {
        signature_data_url: dataUrl,
      });
      toast.success("Manifesto signed. Pillars locked in.");
    } catch (_) {
      toast.message("Manifesto acknowledged locally.");
    }
  };

  const isFinal = stepIdx === STEPS.length - 1;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#04070C" }}>
      <PracticeModeBanner />
      <OpsCenterBackdrop />
      <div className="relative z-10">
        <Header />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
          {/* Hero + Progress */}
          <FlightSchoolHero
            clipKey={step.key}
            gateReady={gateReady}
          />
          <MissionTracker current={stepIdx} />

          {/* Advancing phase wipe */}
          <div
            className={`transition-all duration-500 ${advancing ? "opacity-0 translate-y-3" : "opacity-100 translate-y-0"}`}
            data-testid={`training-step-${step.key}`}
          >
            {/* Command Console + Step content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
              <div className="lg:col-span-5">
                <TrainerConsole
                  stepId={step.id}
                  stepLabel={step.label}
                  line={GREETINGS.amanda?.[step.key] || step.briefing}
                />
              </div>
              <div className="lg:col-span-7">
                <StepContent
                  stepKey={step.key}
                  manifestoSigned={manifestoSigned}
                  manifestoAt={manifestoAt}
                  onManifestoSign={onManifestoSign}
                />
              </div>
            </div>

            {/* Advance action */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="text-[10px] uppercase tracking-[0.32em] text-emerald-300/70 font-mono-aviation">
                Phase {step.id} · {step.label}
              </div>
              {!isFinal ? (
                <button
                  type="button"
                  onClick={advance}
                  disabled={step.key === "philosophy" && !manifestoSigned}
                  data-testid={`training-advance-${step.key}`}
                  className="btn-gold px-6 py-4 rounded-sm text-xs sm:text-sm uppercase tracking-[0.24em] flex items-center justify-center gap-3 min-h-[52px] w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {stepKeyToCta(step.key)}
                  <ChevronRight size={16} />
                </button>
              ) : (
                <Link
                  to="/leader"
                  data-testid="training-enter-leader-dash"
                  className="btn-gold px-6 py-4 rounded-sm text-xs sm:text-sm uppercase tracking-[0.24em] flex items-center justify-center gap-3 min-h-[52px] w-full sm:w-auto"
                >
                  Enter The Grid <ArrowRight size={16} />
                </Link>
              )}
            </div>
          </div>
        </main>

        <FlightSchoolFooter />
      </div>
      <AudioDiagnostic />
    </div>
  );
}

// ------------------------------------------------------------
// Ops Center backdrop — grid + radar sweep
// ------------------------------------------------------------
function OpsCenterBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {/* Base gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 700px at 20% -10%, rgba(16,185,129,0.12), transparent 60%)," +
            "radial-gradient(900px 600px at 100% 10%, rgba(212,175,55,0.09), transparent 60%)," +
            "linear-gradient(180deg, #04070C 0%, #060B14 60%, #04070C 100%)",
        }}
      />
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.20]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(52,211,153,0.10) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(52,211,153,0.10) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 1000px 600px at 50% 45%, black 40%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 1000px 600px at 50% 45%, black 40%, transparent 90%)",
        }}
      />
      {/* Radar sweep (subtle) */}
      <div
        className="absolute -right-40 -top-40 w-[420px] h-[420px] sm:w-[640px] sm:h-[640px] opacity-40"
        style={{
          background:
            "conic-gradient(from 0deg, rgba(52,211,153,0.28) 0deg, transparent 45deg, transparent 360deg)",
          borderRadius: "50%",
          animation: "wingman-radar 6s linear infinite",
        }}
      />
      <style>{`
        @keyframes wingman-radar {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes wingman-scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(1200%); opacity: 0; }
        }
        @keyframes wingman-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ------------------------------------------------------------
// Hero
// ------------------------------------------------------------
function FlightSchoolHero({ clipKey, gateReady }) {
  return (
    <section className="mb-8 sm:mb-10">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="hud-label text-[10px] flex items-center gap-2 mb-3">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                backgroundColor: "#34D399",
                boxShadow: "0 0 12px #34D399",
                animation: "wingman-pulse 1.6s ease-in-out infinite",
              }}
            />
            Wingman Academy · Flight School · Live
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Recruit onboarding for
            <span className="text-gold italic"> field operators</span>.
          </h1>
          <p className="mt-3 text-slate-400 text-sm sm:text-base max-w-2xl leading-relaxed">
            Five phases. One trainer. Zero fluff. By the end of this brief
            you&apos;ll know the mission, the pillars, the arsenal, the primary
            weapon, and how to fire it.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ReplayGreeting character="amanda" label="Replay Amanda" />
            <PracticeModeToggle defaultOn compact />
          </div>
        </div>

        {/* Amanda — sole trainer presenter */}
        <div className="shrink-0 flex lg:justify-end">
          <PresenterBubble
            character="amanda"
            clip={clipKey}
            size={144}
            autoPlay
            gateReady={gateReady}
          />
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------
// Mission Tracker (progress + phase dots)
// ------------------------------------------------------------
function MissionTracker({ current }) {
  const pct = ((current + 1) / STEPS.length) * 100;
  return (
    <div
      className="rounded-sm border p-4 sm:p-5"
      style={{
        borderColor: "rgba(52,211,153,0.35)",
        background:
          "linear-gradient(90deg, rgba(6,11,20,0.85) 0%, rgba(16,185,129,0.06) 60%, rgba(6,11,20,0.85) 100%)",
      }}
      data-testid="mission-tracker"
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="hud-label text-[10px] font-mono-aviation text-emerald-300">
          Mission Phase Tracker
        </div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 font-mono-aviation">
          Step {current + 1} of {STEPS.length}
        </div>
      </div>
      {/* Bar */}
      <div className="h-1.5 rounded-sm bg-white/5 overflow-hidden mb-4">
        <div
          className="h-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background:
              "linear-gradient(90deg, #34D399 0%, #D4AF37 100%)",
            boxShadow: "0 0 14px rgba(52,211,153,0.5)",
          }}
        />
      </div>
      {/* Phase dots */}
      <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
        {STEPS.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              data-testid={`mission-dot-${s.key}`}
              className="flex flex-col items-center text-center"
            >
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-sm border flex items-center justify-center transition-colors"
                style={{
                  borderColor: active
                    ? "#D4AF37"
                    : done
                    ? "#34D399"
                    : "rgba(255,255,255,0.15)",
                  background: active
                    ? "rgba(212,175,55,0.12)"
                    : done
                    ? "rgba(52,211,153,0.10)"
                    : "rgba(6,11,20,0.4)",
                  color: active ? "#D4AF37" : done ? "#34D399" : "#64748B",
                  boxShadow: active ? "0 0 12px rgba(212,175,55,0.35)" : "none",
                }}
              >
                {done ? <CheckCircle2 size={14} /> : <Icon size={14} />}
              </div>
              <div
                className="mt-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-mono-aviation truncate w-full"
                style={{ color: active ? "#F8FAFC" : done ? "#94A3B8" : "#475569" }}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Wingman Command Console (left rail)
// ------------------------------------------------------------
function TrainerConsole({ stepId, stepLabel, line }) {
  // Typewriter effect — cheap + terminal-y.
  const [rendered, setRendered] = useState("");
  useEffect(() => {
    setRendered("");
    let i = 0;
    const total = line.length;
    const speed = Math.max(10, Math.min(24, Math.floor(1500 / Math.max(50, total))));
    const timer = setInterval(() => {
      i += 2;
      setRendered(line.slice(0, i));
      if (i >= total) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [line]);

  return (
    <div
      className="rounded-sm border overflow-hidden h-full flex flex-col"
      style={{
        borderColor: "rgba(52,211,153,0.4)",
        background:
          "linear-gradient(160deg, rgba(2,6,12,0.95) 0%, rgba(6,20,18,0.9) 100%)",
        boxShadow:
          "0 0 0 1px rgba(52,211,153,0.06) inset, 0 20px 40px -20px rgba(16,185,129,0.35)",
      }}
      data-testid="trainer-console"
    >
      {/* Terminal top bar */}
      <div
        className="px-4 py-2.5 flex items-center justify-between border-b"
        style={{
          borderColor: "rgba(52,211,153,0.25)",
          background: "rgba(4,7,12,0.75)",
        }}
      >
        <div className="flex items-center gap-2">
          <Terminal size={14} className="text-emerald-300" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-mono-aviation">
            amanda @ front-counter // phase {stepId.toString().padStart(2, "0")}
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ animation: "wingman-pulse 1.4s ease-in-out infinite" }} />
          <span className="text-[9px] text-emerald-300/70 font-mono-aviation">LIVE</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 sm:p-6 flex-1 relative">
        {/* Faint scan line */}
        <div
          className="absolute inset-x-0 h-8 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(52,211,153,0.14) 0%, transparent 100%)",
            animation: "wingman-scan 5s linear infinite",
          }}
        />

        <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-emerald-300/80 font-mono-aviation">
          <Volume2 size={11} />
          Trainer briefing · {stepLabel}
        </div>

        <p
          className="text-emerald-100 font-mono-aviation text-[13px] sm:text-[14px] leading-relaxed whitespace-pre-wrap"
          style={{ textShadow: "0 0 12px rgba(52,211,153,0.35)" }}
          data-testid="trainer-briefing-text"
        >
          {rendered}
          <span
            className="inline-block w-2 h-3.5 ml-0.5 align-middle"
            style={{
              background: "#34D399",
              boxShadow: "0 0 8px #34D399",
              animation: "wingman-pulse 0.9s ease-in-out infinite",
            }}
          />
        </p>

        <div className="mt-6 pt-4 border-t border-emerald-500/15 flex items-center gap-3">
          <ReplayGreeting
            character="amanda"
            line={line}
            label="Replay Amanda"
            variant="compact"
          />
          <span className="text-[9px] uppercase tracking-[0.28em] text-emerald-300/50 font-mono-aviation">
            audio · en-US
          </span>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Right rail — per-step content
// ------------------------------------------------------------
function StepContent({ stepKey, manifestoSigned, manifestoAt, onManifestoSign }) {
  if (stepKey === "mission") return <StepMission />;
  if (stepKey === "philosophy")
    return (
      <StepPhilosophy
        manifestoSigned={manifestoSigned}
        manifestoAt={manifestoAt}
        onManifestoSign={onManifestoSign}
      />
    );
  if (stepKey === "arsenal") return <StepArsenal />;
  if (stepKey === "streetstrike") return <StepStreetStrike />;
  if (stepKey === "tools") return <StepTools />;
  return <StepActivation />;
}

// -------- Step · Street Strike (Sterling) ---------------------
function StepStreetStrike() {
  return (
    <div className="space-y-5" data-testid="step-streetstrike-content">
      <div className="glass-strong rounded-sm p-5 sm:p-6">
        <div className="hud-label text-[10px] mb-4">Street Strike · Phase 4</div>
        <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
          Own the <span className="text-gold italic">block</span>, then climb the ladder.
        </h2>
        <p className="mt-3 text-slate-300 text-sm leading-relaxed">
          Every rep runs a zip. The dot is the door. The $97 Anchor gets you
          inside — after that, you&apos;re just climbing rungs on the ladder.
        </p>
      </div>
      <StreetStrikeMap />
    </div>
  );
}

// -------- Step 1 · The Mission -------------------------------
function StepMission() {
  const pillars = [
    {
      icon: Target,
      title: "Who we serve",
      body: "Main Street operators — the mechanic, the campground owner, the salon, the roofer. The 50 miles no one drives to.",
    },
    {
      icon: Zap,
      title: "What we deliver",
      body: "A 24/7 A-I concierge, a branded mascot, and an automated follow-up loop — installed in days, not months.",
    },
    {
      icon: Rocket,
      title: "Your role",
      body: "Field operator. You surface prospects, run the Hunt, approve the pitch. The Grid handles the rest.",
    },
  ];
  return (
    <div className="glass-strong rounded-sm p-5 sm:p-6" data-testid="step-mission-content">
      <div className="hud-label text-[10px] mb-4">Mission Briefing · Phase 1</div>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        The <span className="text-gold italic">Last 50 Mile</span> Utility.
      </h2>
      <p className="mt-3 text-slate-300 text-sm leading-relaxed">
        Every Fortune-500 has a full A-I stack. Every mom-and-pop shop still
        misses calls at 8pm. That gap is the mission. You close it.
      </p>
      <div className="mt-6 space-y-3">
        {pillars.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.title}
              className="flex items-start gap-3 rounded-sm border border-white/10 bg-white/[0.02] p-3 sm:p-4"
              data-testid={`mission-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="w-9 h-9 shrink-0 rounded-sm border flex items-center justify-center"
                style={{
                  borderColor: "rgba(212,175,55,0.45)",
                  background: "rgba(212,175,55,0.08)",
                  color: "#D4AF37",
                }}
              >
                <Icon size={16} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-white text-sm">{p.title}</div>
                <div className="text-[12px] text-slate-400 leading-relaxed mt-0.5">{p.body}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------- Step · Grid Philosophy ------------------------------
const PILLARS = [
  {
    n: "01",
    icon: Bolt,
    title: "We are a Utility, not an App",
    body:
      "We don't sell software. We install an AI operations team into a small business — like a power company installs a meter.",
    accent: "#D4AF37",
  },
  {
    n: "02",
    icon: Globe2,
    title: "We lead with \"Wow\"",
    body:
      "Every touchpoint hits the National Geographic Standard — so good it stops people mid-scroll and makes them say I need that.",
    accent: "#34D399",
  },
  {
    n: "03",
    icon: Bot,
    title: "AI is a Team, not a tool",
    body:
      "Amanda runs the front counter. Alfred handles strategy. Bentley owns the adventure. The prospect never feels alone.",
    accent: "#60A5FA",
  },
  {
    n: "04",
    icon: Clock,
    title: "We sell Time Recovery",
    body:
      "The ROI isn't more leads — it's giving owners back seven hours a week they're burning on follow-ups, scheduling, and chasing no-shows.",
    accent: "#F5C842",
  },
];

function StepPhilosophy({ manifestoSigned, manifestoAt, onManifestoSign }) {
  return (
    <div className="glass-strong rounded-sm p-5 sm:p-6" data-testid="step-philosophy-content">
      <div className="hud-label text-[10px] mb-4">Grid Philosophy · Phase 2</div>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        The <span className="text-gold italic">Four Pillars</span>.
      </h2>
      <p className="mt-3 text-slate-300 text-sm leading-relaxed">
        This is who we are and what we sell. Memorize the pillars — every
        pitch you write must echo at least one of them.
      </p>

      {/* Ohio grid — holographic coverage map */}
      <div className="mt-5">
        <OhioGridMini />
        <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-aviation">
          Three nodes. One shared spine. The Last 50 Miles of coverage.
        </div>
      </div>

      {/* Pillars */}
      <div className="mt-6 space-y-3">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.n}
              className="rounded-sm border p-3 sm:p-4 flex items-start gap-3"
              style={{
                borderColor: `${p.accent}55`,
                background: "linear-gradient(160deg, rgba(6,11,20,0.6), rgba(6,11,20,0.35))",
              }}
              data-testid={`pillar-${p.n}`}
            >
              <div
                className="w-10 h-10 shrink-0 rounded-sm border flex items-center justify-center"
                style={{
                  borderColor: `${p.accent}88`,
                  background: `${p.accent}18`,
                  color: p.accent,
                }}
              >
                <Icon size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-3 mb-1">
                  <span
                    className="text-[10px] uppercase tracking-[0.28em] font-mono-aviation"
                    style={{ color: p.accent }}
                  >
                    Pillar {p.n}
                  </span>
                  <span className="font-display text-white text-sm sm:text-base leading-tight">
                    {p.title}
                  </span>
                </div>
                <div className="text-[12px] text-slate-400 leading-relaxed">
                  {p.body}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Practice Mode notice */}
      <div
        className="mt-6 rounded-sm border p-3 sm:p-4 flex items-start gap-3"
        style={{
          borderColor: "rgba(245,200,66,0.45)",
          background:
            "linear-gradient(160deg, rgba(212,175,55,0.10) 0%, rgba(6,11,20,0.4) 100%)",
        }}
        data-testid="philosophy-practice-notice"
      >
        <Radar size={14} className="text-gold mt-0.5 shrink-0" />
        <div className="text-[12px] text-slate-200 leading-relaxed">
          <span className="text-gold uppercase tracking-[0.24em] font-mono-aviation text-[10px] mr-2">
            Notice
          </span>
          After this briefing you&apos;ll enter the Simulator (Practice Mode).
          Everything you do there is real AI research — but no live pitches
          will fire until your Commander clears you for duty.
        </div>
      </div>

      {/* Manifesto acknowledgment */}
      <div
        className="mt-6 rounded-sm border p-4 sm:p-5"
        style={{
          borderColor: "rgba(212,175,55,0.55)",
          background:
            "linear-gradient(180deg, rgba(6,11,20,0.9) 0%, rgba(212,175,55,0.08) 100%)",
        }}
        data-testid="manifesto-card"
      >
        <div className="text-[10px] uppercase tracking-[0.32em] font-mono-aviation text-gold mb-3">
          Manifesto · Acknowledgment
        </div>
        <ul className="space-y-1.5 mb-5">
          {PILLARS.map((p) => (
            <li key={`man-${p.n}`} className="text-[12px] text-slate-300 flex gap-2 items-start">
              <span className="text-gold font-mono-aviation text-[10px] mt-1">▸</span>
              <span>
                <span className="text-white">{p.title}.</span>{" "}
                <span className="text-slate-500">Pillar {p.n}.</span>
              </span>
            </li>
          ))}
        </ul>
        <SignatureCanvas
          onSign={onManifestoSign}
          signed={manifestoSigned}
          signedAt={manifestoAt}
        />
      </div>
    </div>
  );
}

// -------- Step 2 · The Arsenal -------------------------------
const ARSENAL_ITEMS = [
  {
    id: "moto",
    kind: "Live Demo",
    title: "Motorcycle Foyer",
    sub: "System B · full 12-section engine · the killer close",
    href: "/moto",
    accent: "#DC2626",
    icon: Bike,
    hook: "Send when the prospect is a shop, garage, or trade.",
  },
  {
    id: "bentley",
    kind: "Live Demo",
    title: "Bentley — Adventure Captain",
    sub: "Campground vertical · Wooster node · warm hospitality",
    href: "/bentley",
    accent: "#34D399",
    icon: Sparkles,
    hook: "Send when the prospect is hospitality, camp, or tourism.",
  },
  {
    id: "mirror",
    kind: "Live Demo",
    title: "Magic Mirror",
    sub: "Instant business preview · works for any trade",
    href: "/demo",
    accent: "#60A5FA",
    icon: Radio,
    hook: "Send when you can't get a call — self-serve preview.",
  },
  {
    id: "ebook",
    kind: "Asset",
    title: "2026 Local AI Growth Roadmap",
    sub: "E-book · quiet download → warm lead in Coordinator Inbox",
    href: "/local",
    accent: "#D4AF37",
    icon: BookOpen,
    hook: "Send when the prospect wants to think it over — captures them anyway.",
  },
];

function StepArsenal() {
  return (
    <div className="glass-strong rounded-sm p-5 sm:p-6" data-testid="step-arsenal-content">
      <div className="hud-label text-[10px] mb-4">Arsenal Walkthrough · Phase 2</div>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        Your <span className="text-gold italic">Resource Library</span>.
      </h2>
      <p className="mt-3 text-slate-300 text-sm leading-relaxed">
        Four hooks. One rule: don&apos;t lecture — send the link that does the
        talking. Every asset is live, brandable, and instrumented.
      </p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ARSENAL_ITEMS.map((it) => (
          <ArsenalCard key={it.id} item={it} />
        ))}
      </div>
      <div
        className="mt-5 rounded-sm border p-3 sm:p-4 flex items-start gap-3"
        style={{
          borderColor: "rgba(52,211,153,0.35)",
          background: "rgba(16,185,129,0.05)",
        }}
      >
        <ShieldCheck size={14} className="text-emerald-300 mt-0.5 shrink-0" />
        <div className="text-[12px] text-slate-300 leading-relaxed">
          <span className="text-emerald-300 font-mono-aviation">TACTIC:</span>{" "}
          Every asset link routes back to a Coordinator Inbox capture. If the
          prospect opens it — even without booking — it lands on the Leader
          Dashboard as a warm signal. You never lose a click.
        </div>
      </div>
    </div>
  );
}

function ArsenalCard({ item }) {
  const Icon = item.icon;
  return (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`arsenal-card-${item.id}`}
      className="group rounded-sm border border-white/10 hover:border-gold/60 overflow-hidden transition-all flex flex-col"
    >
      <div
        className="relative h-24 border-b border-white/10 flex items-end p-3"
        style={{
          background: `linear-gradient(135deg, ${item.accent}26 0%, rgba(6,11,20,0.9) 70%)`,
        }}
      >
        <div
          className="absolute top-2 left-2 px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.24em] font-mono-aviation border"
          style={{
            borderColor: `${item.accent}88`,
            color: item.accent,
            background: "rgba(6,11,20,0.85)",
          }}
        >
          {item.kind}
        </div>
        <div
          className="absolute top-2 right-2 w-8 h-8 rounded-sm border flex items-center justify-center"
          style={{
            borderColor: `${item.accent}88`,
            color: item.accent,
            background: "rgba(6,11,20,0.85)",
          }}
        >
          <Icon size={14} />
        </div>
        <div className="font-display text-white text-sm leading-tight group-hover:text-gold transition-colors">
          {item.title}
        </div>
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[11px] text-slate-400 leading-relaxed">{item.sub}</div>
        <div className="mt-2 text-[10px] uppercase tracking-[0.22em] font-mono-aviation" style={{ color: item.accent }}>
          {item.hook}
        </div>
      </div>
    </a>
  );
}

// -------- Step 4 · Tool Training (real interactive Lead Hunter) -----
function StepTools() {
  const hotspots = [
    { key: "url",     label: "① URL Input",       body: "Paste any real site or email. Alfred normalises it and pulls the page in eight seconds." },
    { key: "hunt",    label: "② HUNT Button",     body: "One click. Twelve seconds. Claude Sonnet audits the whole domain." },
    { key: "leaks",   label: "③ Leak Briefing",   body: "Three cards. Red = active bleed. Amber = weakness. Read them before you send." },
    { key: "approve", label: "④ Approve & Queue", body: "Practice Mode is locked ON here — clicking Approve pops the Simulator success modal instead of firing a live pitch." },
  ];
  return (
    <div className="space-y-5" data-testid="step-tools-content">
      <div className="glass-strong rounded-sm p-5 sm:p-6">
        <div className="hud-label text-[10px] mb-4">Guided Hunt · Phase 4 · Simulator</div>
        <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
          The <span className="text-gold italic">Lead Hunter</span> primary weapon.
        </h2>
        <p className="mt-3 text-slate-300 text-sm leading-relaxed">
          Type a real prospect URL and hit HUNT. Alfred will audit them live —
          this is not a mock-up. Practice Mode is locked ON so no live pitch
          fires, but every leak briefing you see is genuine AI research.
        </p>

        {/* Numbered hotspot guide */}
        <div className="mt-5 space-y-2.5" data-testid="tool-hotspots">
          {hotspots.map((h) => (
            <div
              key={h.key}
              className="flex items-start gap-3 rounded-sm border border-emerald-500/25 bg-emerald-500/[0.04] p-3"
              data-testid={`hotspot-${h.key}`}
            >
              <div className="text-[10px] uppercase tracking-[0.24em] font-mono-aviation text-emerald-300 min-w-[90px]">
                {h.label}
              </div>
              <div className="text-[12px] text-slate-300 leading-relaxed">{h.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* REAL Lead Hunter, practice-mode-locked */}
      <div data-testid="training-live-lead-hunter">
        <LeadHunter forcePractice />
      </div>
    </div>
  );
}

// -------- Step 4 · Activation --------------------------------
function StepActivation() {
  return (
    <div className="glass-strong rounded-sm p-5 sm:p-6" data-testid="step-activation-content">
      <div className="hud-label text-[10px] mb-4">Activation Pipeline · Phase 4</div>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        The <span className="text-gold italic">Grid Activation Bridge</span>.
      </h2>
      <p className="mt-3 text-slate-300 text-sm leading-relaxed">
        Approve a pitch. Three things happen automatically.
      </p>

      <ol className="mt-5 space-y-3">
        {[
          {
            n: "01",
            title: "Pitch is stamped GVO-ready",
            body: "Server marks the hunt approved + queued. Timestamped for audit.",
          },
          {
            n: "02",
            title: "Auto-Net picks it up",
            body: "The GVO outreach pipeline consumes the queue and dispatches your pitch on your behalf.",
          },
          {
            n: "03",
            title: "Warm signals surface in the Coordinator Inbox",
            body: "Opens, clicks, replies — all routed to the Leader Dashboard for follow-up.",
          },
        ].map((row) => (
          <li
            key={row.n}
            className="flex items-start gap-3 rounded-sm border border-white/10 bg-white/[0.02] p-3 sm:p-4"
            data-testid={`activation-row-${row.n}`}
          >
            <div className="text-gold font-display text-2xl leading-none min-w-[42px]">
              {row.n}
            </div>
            <div>
              <div className="font-display text-white text-sm">{row.title}</div>
              <div className="text-[12px] text-slate-400 leading-relaxed mt-0.5">
                {row.body}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {/* Cleared-for-duty badge */}
      <div
        className="mt-6 rounded-sm border p-4 sm:p-5 relative overflow-hidden"
        style={{
          borderColor: "rgba(212,175,55,0.55)",
          background:
            "linear-gradient(135deg, rgba(212,175,55,0.14) 0%, rgba(16,185,129,0.10) 60%, rgba(6,11,20,0.9) 100%)",
        }}
        data-testid="cleared-for-duty-badge"
      >
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(212,175,55,0.85), transparent)",
          }}
        />
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div
            className="w-14 h-14 rounded-full border flex items-center justify-center shrink-0"
            style={{
              borderColor: "rgba(212,175,55,0.7)",
              background: "rgba(212,175,55,0.15)",
              color: "#D4AF37",
              boxShadow: "0 0 24px rgba(212,175,55,0.35)",
            }}
          >
            <ShieldCheck size={26} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] font-mono-aviation text-emerald-300">
              Certification Complete
            </div>
            <div className="font-display text-white text-lg sm:text-xl leading-tight mt-1">
              You&apos;re <span className="text-gold italic">cleared for duty</span>, operator.
            </div>
            <div className="text-[12px] text-slate-400 mt-1">
              Enter the Leader Dashboard and run your first hunt.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Footer
// ------------------------------------------------------------
function FlightSchoolFooter() {
  return (
    <footer className="mt-10 border-t border-white/10 py-6">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-mono-aviation">
          Wingman Academy · Flight School · The Last 50 Mile A-I Utility Co.
        </div>
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.24em] font-mono-aviation">
          <Link to="/" className="text-slate-400 hover:text-gold transition-colors">
            Lobby
          </Link>
          <Link to="/leader" className="text-slate-400 hover:text-gold transition-colors">
            Leader Dashboard
          </Link>
          <a
            href={SPINE_LINKS.FOUNDER_CTA}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-gold transition-colors"
            data-testid="training-footer-founder-link"
          >
            Founder Node
          </a>
        </div>
      </div>
    </footer>
  );
}

function stepKeyToCta(key) {
  if (key === "mission") return "Roger That";
  if (key === "philosophy") return "Manifesto Committed";
  if (key === "arsenal") return "Arsenal Reviewed";
  if (key === "streetstrike") return "Block Scanned";
  if (key === "tools") return "Training Complete";
  return "Enter The Grid";
}
